class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  connectedCallback() {
    this.cartKeydown = (event) => {
      if (!this.ownsFocus()) {
        if ((event.key === 'Escape' || event.code === 'Escape') && this.openTimer) {
          event.preventDefault();
          this.close();
        }
        return;
      }
      if (event.key === 'Escape' || event.code === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        this.close();
      } else if (event.key === 'Tab' || event.code === 'Tab') {
        const items = this.focusableItems();
        const first = items[0];
        const last = items[items.length - 1];
        const active = document.activeElement;
        if (!items.length || !items.includes(active) ||
            (event.shiftKey ? active === first : active === last)) {
          event.preventDefault();
          event.stopPropagation();
          (event.shiftKey ? last : first)?.focus();
          if (!items.length) this.focusDrawer();
        }
      }
    };
    this.cartFocusin = (event) => {
      if (this.ownsFocus() && !this.contains(event.target)) this.repairFocus();
    };
    document.addEventListener('keydown', this.cartKeydown, true);
    document.addEventListener('focusin', this.cartFocusin);
    this.cartFocusout = () => this.scheduleFocusRepair();
    this.addEventListener('focusout', this.cartFocusout);
    this.cartVisibilityChanged = () => this.scheduleFocusRepair();
    this.addEventListener('transitionend', this.cartVisibilityChanged);
    this.cartFocusObserver = new MutationObserver(() => {
      if (!this.ownsFocus()) return;
      // CartItems can install a trap on controls that are replaced or hidden.
      // This drawer owns its dynamic trap; preserve a still-visible focus target.
      removeTrapFocus();
      this.repairFocus();
      this.scheduleFocusRepair();
    });
    this.cartFocusObserver.observe(this, {
      childList: true, subtree: true, attributes: true,
      attributeFilter: ['class', 'style', 'hidden', 'disabled', 'tabindex']
    });
  }

  disconnectedCallback() {
    clearTimeout(this.openTimer);
    this.openTimer = null;
    clearTimeout(this.renderTimer);
    clearTimeout(this.focusRepairTimer);
    this.focusRepairTimer = null;
    document.removeEventListener('keydown', this.cartKeydown, true);
    document.removeEventListener('focusin', this.cartFocusin);
    this.cartFocusObserver?.disconnect();
    this.removeEventListener('focusout', this.cartFocusout);
    this.removeEventListener('transitionend', this.cartVisibilityChanged);
    if (this.classList.contains('active')) {
      this.classList.remove('active');
      removeTrapFocus();
      document.body.classList.remove('overflow-hidden');
    }
  }

  ownsFocus() {
    // Shared search/menu/guide layers retain their own keyboard handling.
    return this.isConnected && this.classList.contains('active') &&
      !this.closest('[inert]') && !window.JapanScissors?.layers?.top();
  }

  focusableItems() {
    const dialog = this.querySelector('.drawer__inner');
    return dialog ? getFocusableElements(dialog).filter((element) =>
      element.tabIndex >= 0 && !element.matches(':disabled') &&
      !element.closest('[inert]') && element.getClientRects().length > 0 &&
      getComputedStyle(element).visibility !== 'hidden'
    ) : [];
  }

  focusDrawer() {
    const target = this.focusableItems()[0] || this.querySelector('.drawer__inner');
    if (!target || !target.getClientRects().length || getComputedStyle(target).visibility === 'hidden') return false;
    target.focus({ preventScroll: true });
    return document.activeElement === target;
  }

  repairFocus() {
    if (!this.ownsFocus()) return false;
    const active = document.activeElement;
    const dialog = this.querySelector('.drawer__inner');
    if (this.focusableItems().includes(active) || (active === dialog &&
        dialog.getClientRects().length && getComputedStyle(dialog).visibility !== 'hidden')) return true;
    return this.focusDrawer();
  }

  scheduleFocusRepair() {
    clearTimeout(this.focusRepairTimer);
    this.focusRepairTimer = null;
    if (!this.ownsFocus()) return;
    let attempts = 0;
    const repair = () => {
      this.focusRepairTimer = null;
      if (!this.ownsFocus()) return;
      // Visibility can lag .active, and a focused empty-state node can disappear
      // after the mutation callback. Check actual focus on the next task too.
      if (!this.repairFocus() && ++attempts < 20) {
        this.focusRepairTimer = setTimeout(repair, 50);
      }
    };
    this.focusRepairTimer = setTimeout(repair, 0);
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    else if (!this.activeElement?.isConnected || this.activeElement === document.body || this.contains(this.activeElement)) {
      const current = document.activeElement;
      this.setActiveElement(current !== document.body && !this.contains(current)
        ? current : document.querySelector('#cart-icon-bubble'));
    }
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    clearTimeout(this.openTimer);
    this.openTimer = setTimeout(() => {
      this.openTimer = null;
      if (!this.isConnected) return;
      this.classList.add('animate', 'active');
      document.body.classList.add('overflow-hidden');
      // Focus must not depend on transitionend: reduced motion, re-opening and
      // section replacement can all omit that event. Boundaries are read anew
      // on every Tab so cart updates cannot leave a trap on detached controls.
      if (this.ownsFocus()) {
        removeTrapFocus();
        this.repairFocus();
        this.scheduleFocusRepair();
      }
    });
  }

  close() {
    clearTimeout(this.openTimer);
    this.openTimer = null;
    clearTimeout(this.renderTimer);
    clearTimeout(this.focusRepairTimer);
    this.focusRepairTimer = null;
    const wasOpen = this.classList.contains('active');
    this.classList.remove('active');
    if (!wasOpen) return;
    document.body.classList.remove('overflow-hidden');
    const opener = this.activeElement?.isConnected && this.activeElement !== document.body && !this.contains(this.activeElement)
      ? this.activeElement : document.querySelector('#cart-icon-bubble');
    removeTrapFocus(opener);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    clearTimeout(this.renderTimer);
    this.renderTimer = setTimeout(() => {
      if (!this.isConnected) return;
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
